import { Modal as AntModal, ConfigProvider } from 'antd';
import { Colors } from 'const/colors';
import React, { ReactNode } from 'react';

export interface ModalProps {
  open: boolean;
  onCancel: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  centered?: boolean;
  closable?: boolean;
}

/**
 * Base Modal Component using Ant Design's Modal.
 *
 * @param props - ModalProps for configuring the modal.
 * @returns A styled modal dialog.
 */
export function Modal({
  open,
  onCancel,
  title,
  children,
  footer,
  className = '',
  centered = true,
  closable = false,
}: ModalProps) {
  return (
    <AntModal
      open={open}
      onCancel={onCancel}
      className={className}
      centered={centered}
      closable={closable}
      title={title}
      footer={footer}
    >
      {children}
    </AntModal>
  );
}

/**
 * Modal component with a blue header and white title text.
 *
 * @param props - ModalProps for configuring the modal.
 * @returns A modal dialog with blue header styling.
 */
export function BlueModal(props: ModalProps) {
  return (
    <ConfigProvider
      theme={{
        components: {
          Modal: {
            headerBg: Colors.BleuHellio,
            titleColor: Colors.white,
          },
        },
      }}
    >
      <Modal {...props} />
    </ConfigProvider>
  );
}

/**
 * Modal component with a white header and blue title text.
 *
 * @param props - ModalProps for configuring the modal.
 * @returns A modal dialog with white header styling.
 */
export function WhiteModal(props: ModalProps) {
  return (
    <ConfigProvider
      theme={{
        components: {
          Modal: {
            headerBg: Colors.white,
            titleColor: Colors.BleuHellio,
            contentBg: Colors.white,
          },
        },
      }}
    >
      <Modal {...props} className={`white-modal ${props.className || ''}`} />
    </ConfigProvider>
  );
}

export interface SmallModalProps extends Omit<ModalProps, 'title'> {
  title?: ReactNode;
  zIndex?: number;
}

/**
 * Compact modal variant with fixed small width and rounded body.
 *
 * @param props - SmallModalProps for configuring the modal.
 * @returns A compact modal dialog.
 */
export function SmallModal({
  open,
  onCancel,
  title = null,
  children,
  footer = null,
  className = '',
  centered = true,
  closable = true,
  zIndex,
}: SmallModalProps) {
  const baseClassName = '[&_.ant-modal-body]:!rounded-t-xl [&&]:!w-[520px]';

  return (
    <AntModal
      open={open}
      onCancel={onCancel}
      className={`${baseClassName} ${className}`.trim()}
      centered={centered}
      closable={closable}
      title={title}
      footer={footer}
      zIndex={zIndex}
    >
      {children}
    </AntModal>
  );
}

export interface MultiStepModalProps extends Omit<
  ModalProps,
  'title' | 'footer'
> {
  width?: number;
  destroyOnClose?: boolean;
  wrapClassName?: string;
}

// Backwards-compatibility alias (prefer MultiStepModalProps)
export type FormModalProps = MultiStepModalProps;

/**
 * Modal component optimized for multi-step forms.
 * Has no padding, rounded corners, and supports full-height content.
 *
 * @param props - FormModalProps for configuring the modal.
 * @returns A modal dialog optimized for form content.
 */
export function MultiStepModal({
  open,
  onCancel,
  children,
  className = '',
  width = 900,
  destroyOnClose = true,
  centered = true,
  wrapClassName,
}: MultiStepModalProps) {
  return (
    <AntModal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={width}
      className={`[&_.ant-modal-content]:p-0 [&_.ant-modal-body]:!p-0 [&_.ant-modal-content]:rounded-xl [&_.ant-modal-content]:overflow-hidden md:[&_.ant-modal-body_.content]:w-full md:[&_.ant-modal-body_.content]:max-w-[700px] ${className}`}
      wrapClassName={wrapClassName}
      destroyOnClose={destroyOnClose}
      centered={centered}
      closable={false}
    >
      {children}
    </AntModal>
  );
}
